import { useEffect, useState } from "react";

import {
  getUploadUrl,
  uploadToS3,
  listFiles,
  getViewUrl,
  getDownloadUrl,
} from "../api/filesService";

// ─── Icons (inline SVG) ────────────────────────────────────────────────────
const Icon = {
  Image: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
    </svg>
  ),
  Upload: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
    </svg>
  ),
  Close: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
    </svg>
  ),
  Download: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
    </svg>
  ),
  ChevronLeft: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z"/>
    </svg>
  ),
  ChevronRight: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
    </svg>
  ),
};

// ─── Section Header ───────────────────────────────────────────────────────
const SectionHeader = ({ icon: IconComp, title, subtitle }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      marginBottom: 20,
    }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        background: "#eff6ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1975d1",
        flexShrink: 0,
      }}
    >
      <IconComp />
    </div>
    <div>
      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 1 }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

// ─── Card wrapper ─────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e8edf3",
      borderRadius: 14,
      padding: 24,
      ...style,
    }}
  >
    {children}
  </div>
);

// ─── Lightbox Modal ────────────────────────────────────────────────────────
function LightboxModal({ image, allImages, onClose, onNavigate }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const currentIndex = allImages.findIndex(img => img.key === image.key);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < allImages.length - 1;

  async function handleDownload() {
    try {
      setIsDownloading(true);
      const downloadData = await getDownloadUrl(image.key);
      
      const link = document.createElement("a");
      link.href = downloadData.url;
      link.download = image.fileName || "imagen.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setIsDownloading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft" && canGoPrev) onNavigate(-1);
    if (e.key === "ArrowRight" && canGoNext) onNavigate(1);
  }

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canGoPrev, canGoNext]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          background: "rgba(255, 255, 255, 0.1)",
          border: "none",
          color: "#fff",
          cursor: "pointer",
          borderRadius: 8,
          padding: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
          zIndex: 10001,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
        }}
      >
        <Icon.Close />
      </button>

      {/* Download Button */}
      <button
        onClick={handleDownload}
        disabled={isDownloading}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          background: "#1975d1",
          border: "none",
          color: "#fff",
          cursor: isDownloading ? "not-allowed" : "pointer",
          borderRadius: 8,
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontWeight: 600,
          fontSize: 13,
          transition: "all 0.2s ease",
          zIndex: 10001,
          opacity: isDownloading ? 0.7 : 1,
        }}
        onMouseEnter={(e) => {
          if (!isDownloading) e.currentTarget.style.background = "#1465b8";
        }}
        onMouseLeave={(e) => {
          if (!isDownloading) e.currentTarget.style.background = "#1975d1";
        }}
      >
        <Icon.Download />
        {isDownloading ? "Descargando..." : "Descargar"}
      </button>

      {/* Main Image */}
      <div
        style={{
          position: "relative",
          maxWidth: "90vw",
          maxHeight: "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={image.url}
          alt="Vista ampliada"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            borderRadius: 12,
            objectFit: "contain",
          }}
        />
      </div>

      {/* Navigation */}
      {allImages.length > 1 && (
        <>
          {canGoPrev && (
            <button
              onClick={() => onNavigate(-1)}
              style={{
                position: "absolute",
                left: 24,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255, 255, 255, 0.1)",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                borderRadius: 8,
                padding: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              }}
            >
              <Icon.ChevronLeft />
            </button>
          )}

          {canGoNext && (
            <button
              onClick={() => onNavigate(1)}
              style={{
                position: "absolute",
                right: 24,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255, 255, 255, 0.1)",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                borderRadius: 8,
                padding: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              }}
            >
              <Icon.ChevronRight />
            </button>
          )}
        </>
      )}

      {/* Counter */}
      <div
        style={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(255, 255, 255, 0.1)",
          color: "#fff",
          padding: "8px 16px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {currentIndex + 1} de {allImages.length}
      </div>
    </div>
  );
}

export default function GalleryRow({
  branchId
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Prefijo relativo a ENV (sin incluir development/)
  const s3Prefix = `gallery/${branchId}/`;

  // Agrupar imágenes por fecha
  const groupedImages = images.reduce((acc, img) => {
    const date = img.lastModified || "Sin fecha";
    if (!acc[date]) acc[date] = [];
    acc[date].push(img);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedImages).sort().reverse();

  async function loadImages() {
    try {
      const files = await listFiles(s3Prefix);

      const mapped = await Promise.all(
        files.map(async (file) => {
          const key = file.key || file.Key;
          const data = await getViewUrl(key);

          return {
            key,
            url: data.url,
            fileName: file.fileName || key.split("/").pop(),
            lastModified: file.lastModified || new Date().toISOString().split("T")[0],
          };
        })
      );

      setImages(mapped);
    } catch (err) {
      console.error("Error loading images:", err);
    }
  }

  useEffect(() => {
    loadImages();
  }, [branchId]);

  async function handleUpload(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const relativeFolder = `gallery/${branchId}`;

        const response = await getUploadUrl(file, relativeFolder);
        await uploadToS3(response.uploadUrl, file);
        
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      await loadImages();
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files?.length) {
      handleUpload({ target: { files } });
    }
  }

  function handleNavigateImage(direction) {
    const index = images.findIndex(img => img.key === selectedImage.key);
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setSelectedImage(images[newIndex]);
    }
  }

  return (
    <Card>
      <SectionHeader
        icon={Icon.Image}
        title="Galería de evidencias"
        subtitle={images.length > 0 ? `${images.length} foto${images.length !== 1 ? 's' : ''}` : "Fotografías de servicios realizados"}
      />

      {/* Upload Area */}
      <label
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 140,
          border: `2px dashed ${dragActive ? "#1975d1" : "#e2e8f0"}`,
          borderRadius: 10,
          padding: 20,
          background: dragActive ? "#eff6ff" : "#f8fafc",
          cursor: "pointer",
          transition: "all 0.2s ease",
          marginBottom: 20,
        }}
      >
        <input
          hidden
          multiple
          type="file"
          accept="image/*"
          onChange={handleUpload}
          disabled={loading}
        />

        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: "#dbeafe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1975d1",
              margin: "0 auto 12px",
            }}
          >
            <Icon.Image />
          </div>
          <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>
            {loading ? "Subiendo imágenes..." : "Arrastra aquí o haz clic para seleccionar"}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            PNG, JPG, GIF hasta 10MB
          </div>
          
          {uploadProgress !== null && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  width: 200,
                  height: 4,
                  background: "#e2e8f0",
                  borderRadius: 2,
                  overflow: "hidden",
                  margin: "0 auto 8px",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    background: "#1975d1",
                    width: `${uploadProgress}%`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>
      </label>

      {/* Gallery Grid - Grouped by Date */}
      {images.length === 0 ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
            background: "#f8fafc",
            border: "1px dashed #e2e8f0",
            borderRadius: 10,
            color: "#94a3b8",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Aún no hay imágenes. ¡Sube las primeras!
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {sortedDates.map((date) => (
            <div key={date}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 12,
                  paddingBottom: 8,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                {date}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 12,
                }}
              >
                {groupedImages[date].map((img, idx) => (
                  <button
                    key={img.key}
                    onClick={() => setSelectedImage(img)}
                    style={{
                      position: "relative",
                      borderRadius: 10,
                      overflow: "hidden",
                      border: "1px solid #e8edf3",
                      background: "#f8fafc",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      padding: 0,
                      aspectRatio: "1",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#1975d1";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 117, 209, 0.15)";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e8edf3";
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <img
                      src={img.url}
                      alt={`Imagen ${idx + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Counter */}
      {images.length > 0 && (
        <div
          style={{
            marginTop: 20,
            padding: "12px 0",
            borderTop: "1px solid #f1f5f9",
            fontSize: 12,
            color: "#64748b",
            fontWeight: 500,
          }}
        >
          {images.length} {images.length === 1 ? "imagen" : "imágenes"} cargada{images.length === 1 ? "" : "s"}
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedImage && (
        <LightboxModal
          image={selectedImage}
          allImages={images}
          onClose={() => setSelectedImage(null)}
          onNavigate={handleNavigateImage}
        />
      )}
    </Card>
  );
}